import { useState } from "react";

import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";

interface SupportFormProps {
  onSuccess?: () => void;
  onClose?: () => void;
}

const topics = ["Bug Report", "Feature Request", "Account Issue", "Billing", "Other"];
const products = ["Clawcraft Platform", "Telegram Integration", "API", "Other"];
const priorities = ["Low", "Medium", "High", "Critical"];

const inputClass =
  "w-full rounded-xl border-2 border-black/20 bg-transparent px-4 py-3 text-sm text-black placeholder:text-black/30 focus:border-brand-coral focus:outline-none dark:border-white/20 dark:text-white dark:placeholder:text-white/30 dark:focus:border-brand-coral";

const selectClass = cn(inputClass, "appearance-none cursor-pointer");

export function SupportForm({ onSuccess, onClose }: SupportFormProps) {
  const [submitted, setSubmitted] = useState(false);

  if (submitted) {
    return (
      <div className="py-12 text-center">
        <div className="mb-4 text-4xl">&#10003;</div>
        <h3 className="text-xl font-bold text-black dark:text-white">Request Submitted</h3>
        <p className="mt-2 text-sm text-black/60 dark:text-white/60">
          We&apos;ll get back to you within 24 hours.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setSubmitted(true);
        onSuccess?.();
      }}
      className="space-y-5"
    >
      <div>
        <label className="mb-1.5 block text-sm font-bold text-black dark:text-white">Email</label>
        <input type="email" required placeholder="you@example.com" className={inputClass} />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1.5 block text-sm font-bold text-black dark:text-white">Topic</label>
          <select required className={selectClass} defaultValue="">
            <option value="" disabled>Select topic...</option>
            {topics.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-bold text-black dark:text-white">Product</label>
          <select required className={selectClass} defaultValue="">
            <option value="" disabled>Select product...</option>
            {products.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
      </div>

      <div>
        <label className="mb-1.5 block text-sm font-bold text-black dark:text-white">Description</label>
        <textarea
          required
          rows={4}
          placeholder="Describe the issue or request..."
          className={cn(inputClass, "resize-none")}
        />
      </div>

      <div>
        <label className="mb-1.5 block text-sm font-bold text-black dark:text-white">
          Steps to Reproduce <span className="font-normal text-black/40 dark:text-white/40">(optional)</span>
        </label>
        <textarea
          rows={3}
          placeholder="1. Go to...&#10;2. Click on...&#10;3. See error..."
          className={cn(inputClass, "resize-none")}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1.5 block text-sm font-bold text-black dark:text-white">Priority</label>
          <select className={selectClass} defaultValue="Medium">
            {priorities.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-bold text-black dark:text-white">
            Attachment <span className="font-normal text-black/40 dark:text-white/40">(optional)</span>
          </label>
          <div className={cn(inputClass, "flex cursor-pointer items-center justify-center border-dashed py-3 text-center text-black/40 dark:text-white/40")}>
            <input type="file" className="hidden" id="support-attachment" />
            <label htmlFor="support-attachment" className="cursor-pointer">
              Drop file or click to upload
            </label>
          </div>
        </div>
      </div>

      <Button type="submit" className="w-full">Submit Request</Button>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="w-full rounded-xl py-3 text-sm font-bold text-black/50 transition-colors hover:text-black lg:hidden dark:text-white/50 dark:hover:text-white"
        >
          Close
        </button>
      )}
    </form>
  );
}
